import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import {
  Plus,
  ArrowLeft,
  Search,
  Clock,
  Timer,
  Hammer,
  Pencil,
  Trash2,
  Settings,
  Calendar,
  User,
  Filter
} from "lucide-react";
import { usePermissions } from "../../hooks/usePermissions";
import { handleApiError } from "../../utils/errorHandler";
import { useTranslation } from "../../contexts/I18nContext";

export default function ProduzioneOrePage() {
  const { t } = useTranslation();
  const { tenant } = useParams();
  const navigate = useNavigate();
  const [ore, setOre] = useState([]);
  const [tipiLavorazione, setTipiLavorazione] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Filtri
  const [filtroCommessa, setFiltroCommessa] = useState("__all__");
  const [filtroTipoLav, setFiltroTipoLav] = useState("__all_types__");
  const [filtroDataDa, setFiltroDataDa] = useState("");
  const [filtroDataA, setFiltroDataA] = useState("");
  
  // Dialog states
  const [showOreDialog, setShowOreDialog] = useState(false);
  const [showTipoDialog, setShowTipoDialog] = useState(false);
  const [editingTipo, setEditingTipo] = useState(null);
  
  // Form states
  const [oreForm, setOreForm] = useState({
    commessa_id: "",
    sottocommessa_id: "",
    tipo_lavorazione_id: "",
    data: new Date().toISOString().split('T')[0],
    ore: "",
    operatore: "",
    note: ""
  });
  
  const [tipoForm, setTipoForm] = useState({
    codice: "",
    nome: "",
    descrizione: "",
    costo_orario: ""
  });
  
  const [sottocommesse, setSottocommesse] = useState([]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      let url = "/tenant/produzione/ore";
      const params = new URLSearchParams();
      if (filtroCommessa && filtroCommessa !== "__all__") params.append("commessa_id", filtroCommessa);
      if (filtroTipoLav && filtroTipoLav !== "__all_types__") params.append("tipo_lavorazione_id", filtroTipoLav);
      if (filtroDataDa) params.append("data_da", filtroDataDa);
      if (filtroDataA) params.append("data_a", filtroDataA);
      if (params.toString()) url += `?${params.toString()}`;

      const [oreRes, tipiRes, commRes] = await Promise.all([
        api.get(url),
        api.get("/tenant/produzione/tipi-lavorazione"),
        api.get("/tenant/produzione/commesse")
      ]);
      
      setOre(oreRes.data);
      setTipiLavorazione(tipiRes.data);
      setCommesse(commRes.data);
    } catch (error) {
      
      handleApiError(error, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [filtroCommessa, filtroTipoLav, filtroDataDa, filtroDataA, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadSottocommesse = async (commessaId) => {
    if (!commessaId) {
      setSottocommesse([]);
      return;
    }
    try {
      const res = await api.get(`/tenant/produzione/commesse/${commessaId}/sottocommesse`);
      setSottocommesse(res.data);
    } catch (error) {
      
    }
  };

  // Ore handlers
  const openOreDialog = () => {
    const activeCommessa = filtroCommessa !== "__all__" ? filtroCommessa : "";
    setOreForm({
      commessa_id: activeCommessa,
      sottocommessa_id: "__main__",
      tipo_lavorazione_id: "",
      data: new Date().toISOString().split('T')[0],
      ore: "",
      operatore: "",
      note: ""
    });
    if (activeCommessa) {
      loadSottocommesse(activeCommessa);
    }
    setShowOreDialog(true);
  };

  const handleSaveOre = async () => {
    if (!oreForm.commessa_id || !oreForm.tipo_lavorazione_id || !oreForm.data || !oreForm.ore) {
      toast.error("Commessa, tipo lavorazione, data e ore sono obbligatori");
      return;
    }
    try {
      await api.post("/tenant/produzione/ore", {
        ...oreForm,
        ore: parseFloat(oreForm.ore),
        sottocommessa_id: oreForm.sottocommessa_id === "__main__" ? null : oreForm.sottocommessa_id
      });
      toast.success("Ore registrate");
      setShowOreDialog(false);
      loadData();
    } catch (error) {
      handleApiError(error, t("errors.saveFailed"));
    }
  };

  const handleDeleteOre = async (id) => {
    if (!confirm(t("confirm.deleteRegistration"))) return;
    try {
      await api.delete(`/tenant/produzione/ore/${id}`);
      toast.success("Registrazione eliminata");
      loadData();
    } catch (error) {
      handleApiError(error, t("errors.deleteFailed"));
    }
  };

  // Tipi Lavorazione handlers
  const openCreateTipoDialog = () => {
    setEditingTipo(null);
    setTipoForm({ codice: "", nome: "", descrizione: "", costo_orario: "" });
    setShowTipoDialog(true);
  };

  const openEditTipoDialog = (tipo) => {
    setEditingTipo(tipo);
    setTipoForm({
      codice: tipo.codice,
      nome: tipo.nome,
      descrizione: tipo.descrizione || "",
      costo_orario: tipo.costo_orario || ""
    });
    setShowTipoDialog(true);
  };

  const handleSaveTipo = async () => {
    if (!tipoForm.codice || !tipoForm.nome) {
      toast.error("Codice e nome obbligatori");
      return;
    }
    try {
      if (editingTipo) {
        await api.put(`/tenant/produzione/tipi-lavorazione/${editingTipo.id}`, {
          ...tipoForm,
          costo_orario: tipoForm.costo_orario ? parseFloat(tipoForm.costo_orario) : null
        });
        toast.success("Tipo lavorazione aggiornato");
      } else {
        await api.post("/tenant/produzione/tipi-lavorazione", {
          ...tipoForm,
          costo_orario: tipoForm.costo_orario ? parseFloat(tipoForm.costo_orario) : null
        });
        toast.success("Tipo lavorazione creato");
      }
      setShowTipoDialog(false);
      loadData();
    } catch (error) {
      handleApiError(error, t("errors.saveFailed"));
    }
  };

  const handleDeleteTipo = async (id) => {
    if (!confirm(t("confirm.deleteWorkType"))) return;
    try {
      await api.delete(`/tenant/produzione/tipi-lavorazione/${id}`);
      toast.success("Tipo lavorazione eliminato");
      loadData();
    } catch (error) {
      handleApiError(error, t("errors.deleteFailed"));
    }
  };

  // Calcoli
  const totaleOre = ore.reduce((sum, o) => sum + (parseFloat(o.ore) || 0), 0);
  const orePerTipo = tipiLavorazione.map(t => ({
    ...t,
    ore: ore.filter(o => o.tipo_lavorazione_id === t.id).reduce((sum, o) => sum + (parseFloat(o.ore) || 0), 0)
  })).filter(t => t.ore > 0);

  return (
    <div className="space-y-6" data-testid="produzione-ore-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/${tenant}/produzione`)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{t("production.workHours")}</h1>
            <p className="text-muted-foreground">Gestisci ore lavorate e tipi di lavorazione</p>
          </div>
        </div>
        <Button onClick={openOreDialog} data-testid="btn-registra-ore">
          <Plus className="h-4 w-4 mr-2" />
          Registra Ore
        </Button>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Ore Totali</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{totaleOre.toFixed(1)}h</div>
            <p className="text-xs text-muted-foreground">{ore.length} registrazioni</p>
          </CardContent>
        </Card>
        {orePerTipo.slice(0, 3).map((t) => (
          <Card key={t.id}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t.nome}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{t.ore.toFixed(1)}h</div>
              <p className="text-xs text-muted-foreground">{t.codice}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="ore" className="space-y-4">
        <TabsList>
          <TabsTrigger value="ore" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Ore Registrate ({ore.length})
          </TabsTrigger>
          <TabsTrigger value="tipi" className="flex items-center gap-2">
            <Hammer className="h-4 w-4" />
            Tipi Lavorazione ({tipiLavorazione.length})
          </TabsTrigger>
        </TabsList>

        {/* Tab Ore */}
        <TabsContent value="ore">
          {/* Filtri */}
          <Card className="mb-4">
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-4">
                <Select value={filtroCommessa} onValueChange={setFiltroCommessa}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder={t("production.allOrders")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Tutte le commesse</SelectItem>
                    {commesse.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.codice} - {c.titolo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filtroTipoLav} onValueChange={setFiltroTipoLav}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder={t("common.allTypes")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all_types__">Tutti i tipi</SelectItem>
                    {tipiLavorazione.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Da:</Label>
                  <Input
                    type="date"
                    value={filtroDataDa}
                    onChange={(e) => setFiltroDataDa(e.target.value)}
                    className="w-[150px]"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm">A:</Label>
                  <Input
                    type="date"
                    value={filtroDataA}
                    onChange={(e) => setFiltroDataA(e.target.value)}
                    className="w-[150px]"
                  />
                </div>
                {(filtroCommessa || filtroTipoLav || filtroDataDa || filtroDataA) && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setFiltroCommessa("");
                      setFiltroTipoLav("");
                      setFiltroDataDa("");
                      setFiltroDataA("");
                    }}
                  >
                    Pulisci filtri
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              {ore.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Timer className="h-12 w-12 mx-auto opacity-50 mb-2" />
                  <p>{t("production.noHours")}</p>
                  <Button className="mt-4" onClick={openOreDialog}>
                    <Plus className="h-4 w-4 mr-2" />
                    Registra Prima Voce
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("common.date")}</TableHead>
                      <TableHead>Commessa</TableHead>
                      <TableHead>Sottocommessa</TableHead>
                      <TableHead>Tipo Lavorazione</TableHead>
                      <TableHead>Operatore</TableHead>
                      <TableHead className="text-right">Ore</TableHead>
                      <TableHead>{t("common.notes")}</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ore.map((o) => (
                      <TableRow key={o.id}>
                        <TableCell>{new Date(o.data).toLocaleDateString('it-IT')}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{o.commessa_codice}</Badge>
                        </TableCell>
                        <TableCell>{o.sottocommessa_codice || "-"}</TableCell>
                        <TableCell>
                          <span className="font-medium">{o.tipo_lavorazione_nome}</span>
                          <span className="text-xs text-muted-foreground ml-1">({o.tipo_lavorazione_codice})</span>
                        </TableCell>
                        <TableCell>{o.operatore || "-"}</TableCell>
                        <TableCell className="text-right font-bold">{o.ore}h</TableCell>
                        <TableCell className="max-w-[200px] truncate">{o.note || "-"}</TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDeleteOre(o.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Tipi Lavorazione */}
        <TabsContent value="tipi">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Tipi di Lavorazione</CardTitle>
              <Button size="sm" onClick={openCreateTipoDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Nuovo Tipo
              </Button>
            </CardHeader>
            <CardContent>
              {tipiLavorazione.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Hammer className="h-12 w-12 mx-auto opacity-50 mb-2" />
                  <p>{t("production.noWorkTypes")}</p>
                  <Button className="mt-4" onClick={openCreateTipoDialog}>
                    <Plus className="h-4 w-4 mr-2" />
                    Crea Primo Tipo
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("common.code")}</TableHead>
                      <TableHead>{t("common.name")}</TableHead>
                      <TableHead>{t("common.description")}</TableHead>
                      <TableHead className="text-right">Costo Orario</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tipiLavorazione.map((t) => (
                      <TableRow key={t.id}>
                        <TableCell className="font-mono">{t.codice}</TableCell>
                        <TableCell className="font-medium">{t.nome}</TableCell>
                        <TableCell className="max-w-[300px] truncate">{t.descrizione || "-"}</TableCell>
                        <TableCell className="text-right">
                          {t.costo_orario ? `€${t.costo_orario.toFixed(2)}/h` : "-"}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => openEditTipoDialog(t)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDeleteTipo(t.id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Registra Ore */}
      <Dialog open={showOreDialog} onOpenChange={setShowOreDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("production.registerHours")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Commessa *</Label>
                <Select 
                  value={oreForm.commessa_id}
                  onValueChange={(v) => {
                    setOreForm({...oreForm, commessa_id: v, sottocommessa_id: ""});
                    loadSottocommesse(v);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("common.selectPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {commesse.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.codice} - {c.titolo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Sottocommessa</Label>
                <Select 
                  value={oreForm.sottocommessa_id}
                  onValueChange={(v) => setOreForm({...oreForm, sottocommessa_id: v})}
                  disabled={!oreForm.commessa_id}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("common.optional")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__main__">Commessa principale</SelectItem>
                    {sottocommesse.map(sc => (
                      <SelectItem key={sc.id} value={sc.id}>{sc.codice} - {sc.titolo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo Lavorazione *</Label>
                <Select 
                  value={oreForm.tipo_lavorazione_id}
                  onValueChange={(v) => setOreForm({...oreForm, tipo_lavorazione_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("common.selectPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {tipiLavorazione.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.codice} - {t.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('common.date')} *</Label>
                <Input
                  type="date"
                  value={oreForm.data}
                  onChange={(e) => setOreForm({...oreForm, data: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ore *</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  value={oreForm.ore}
                  onChange={(e) => setOreForm({...oreForm, ore: e.target.value})}
                  placeholder="es. 8"
                />
              </div>
              <div className="space-y-2">
                <Label>Operatore</Label>
                <Input
                  value={oreForm.operatore}
                  onChange={(e) => setOreForm({...oreForm, operatore: e.target.value})}
                  placeholder={t("production.operatorName")}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t("common.notes")}</Label>
              <Textarea
                value={oreForm.note}
                onChange={(e) => setOreForm({...oreForm, note: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOreDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleSaveOre}>Registra</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Tipo Lavorazione */}
      <Dialog open={showTipoDialog} onOpenChange={setShowTipoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTipo ? t("production.editWorkType") : t("production.newWorkType")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('common.code')} *</Label>
                <Input
                  value={tipoForm.codice}
                  onChange={(e) => setTipoForm({...tipoForm, codice: e.target.value})}
                  placeholder="es. SALD"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('common.name')} *</Label>
                <Input
                  value={tipoForm.nome}
                  onChange={(e) => setTipoForm({...tipoForm, nome: e.target.value})}
                  placeholder="es. Saldatura"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t("common.description")}</Label>
              <Textarea
                value={tipoForm.descrizione}
                onChange={(e) => setTipoForm({...tipoForm, descrizione: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Costo Orario (Euro/h)</Label>
              <Input
                type="number"
                step="0.01"
                value={tipoForm.costo_orario}
                onChange={(e) => setTipoForm({...tipoForm, costo_orario: e.target.value})}
                placeholder={t("common.optional")}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTipoDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleSaveTipo}>{editingTipo ? "Aggiorna" : "Crea"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
